import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Star, MapPin, Truck, ArrowLeft, User } from "lucide-react";

export default function DriverProfile() {
  const { email } = useParams();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [customerReviews, setCustomerReviews] = useState([]);

  useEffect(() => {
    api.get(`/users/${encodeURIComponent(email)}`).then(r => setProfile(r.data));
    api.get("/reviews", { params: { driver_email: email } }).then(r => setReviews(r.data));
    api.get("/customer-reviews", { params: { customer_email: email } }).then(r => setCustomerReviews(r.data)).catch(() => {});
  }, [email]);

  if (!profile) return <div className="max-w-3xl mx-auto px-4 py-20 text-center">Indlæser...</div>;

  const initials = (profile.full_name || profile.email).split(/\s+/).map(s => s[0]).slice(0,2).join("").toUpperCase();
  const hasDriverReviews = reviews.length > 0;
  const hasCustomerReviews = customerReviews.length > 0;
  const defaultTab = hasDriverReviews ? "driver" : hasCustomerReviews ? "customer" : "driver";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/jobs" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Tilbage
      </Link>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4 flex-wrap">
            <Avatar className="w-20 h-20">
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-2xl">{profile.full_name || profile.email}</CardTitle>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {profile.rating !== null && (
                  <span className="flex items-center gap-1 text-sm" data-testid="driver-rating">
                    <Truck className="w-3.5 h-3.5 text-primary" />
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-semibold">{profile.rating}</span>
                    <span className="text-muted-foreground text-xs">som chauffør ({profile.review_count})</span>
                  </span>
                )}
                {profile.customer_rating !== null && (
                  <span className="flex items-center gap-1 text-sm" data-testid="customer-rating">
                    <User className="w-3.5 h-3.5 text-blue-600" />
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-semibold">{profile.customer_rating}</span>
                    <span className="text-muted-foreground text-xs">som kunde ({profile.customer_review_count})</span>
                  </span>
                )}
                {profile.city && <span className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {profile.city}</span>}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {profile.bio && <p className="text-muted-foreground leading-relaxed mb-6">{profile.bio}</p>}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Stat label="Hentede jobs" value={profile.completed_jobs || 0} />
            <Stat label="Som kunde" value={profile.posted_jobs || 0} />
            <Stat label="Chauffør-vurdering" value={profile.rating || "–"} />
            <Stat label="Kunde-vurdering" value={profile.customer_rating || "–"} />
          </div>

          {profile.vehicle_type && (
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm"><Truck className="w-4 h-4 text-muted-foreground" /> <span className="font-medium">{profile.vehicle_type}</span></div>
              {profile.vehicle_description && <p className="text-sm text-muted-foreground">{profile.vehicle_description}</p>}
            </div>
          )}

          {profile.specialties?.length > 0 && (
            <div className="mb-2">
              <p className="text-xs text-muted-foreground mb-2">Specialer</p>
              <div className="flex flex-wrap gap-1.5">
                {profile.specialties.map(s => <Badge key={s} variant="secondary">{s.replace("_", " ")}</Badge>)}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-lg">Anmeldelser</CardTitle></CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="driver" data-testid="tab-driver-reviews">
                <Truck className="w-3.5 h-3.5 mr-1.5" /> Som chauffør ({reviews.length})
              </TabsTrigger>
              <TabsTrigger value="customer" data-testid="tab-customer-reviews">
                <User className="w-3.5 h-3.5 mr-1.5" /> Som kunde ({customerReviews.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="driver">
              <ReviewList reviews={reviews} emptyMsg="Ingen anmeldelser som chauffør endnu" reviewerLabel="reviewer_name" />
            </TabsContent>
            <TabsContent value="customer">
              <ReviewList reviews={customerReviews} emptyMsg="Ingen anmeldelser som kunde endnu" reviewerLabel="driver_name" />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-border p-3 text-center">
      <p className="text-xl font-bold text-primary">{value}</p>
      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{label}</p>
    </div>
  );
}

function ReviewList({ reviews, emptyMsg, reviewerLabel }) {
  if (reviews.length === 0) return <p className="text-sm text-muted-foreground text-center py-6">{emptyMsg}</p>;
  return (
    <div className="space-y-4">
      {reviews.map(r => (
        <div key={r.id} className="pb-4 border-b border-border last:border-0 last:pb-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex gap-0.5">
              {[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
            </div>
            <span className="text-sm font-medium">{r[reviewerLabel] || "Anonym"}</span>
          </div>
          {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
          <p className="text-[11px] text-muted-foreground/70 mt-1">{new Date(r.created_at).toLocaleDateString("da-DK")}</p>
        </div>
      ))}
    </div>
  );
}
